import React, { useEffect } from 'react';
import { Activity, Database, FileCheck, ShieldCheck, Zap, BarChart3, Cpu, ChevronRight, Globe, Layers, LayoutGrid, ExternalLink, ArrowUpRight, MapPin } from 'lucide-react';
import EvalHeader from './EvalHeader';
import FNI from "../Images/FNI.png"
import FIT from "../Images/FIT.png"
import Footer from './footer';


const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white min-h-screen text-slate-900 font-sans selection:bg-indigo-100">
      <EvalHeader />

      {/* --- HERO: THE BRAND SYNERGY --- */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-6xl mx-auto py-16 px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
            <div className="max-w-2xl">
              <h4 className="text-indigo-600 text-[10px] font-black uppercase tracking-[0.3em] mb-4 flex items-center gap-2">
                <Layers size={14} /> Focus NDT Group Ecosystem
              </h4>
              <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-slate-900 mb-6 leading-[1.1]">
                Bridging Industrial Expertise <br />
                with <span className="text-indigo-600">Digital Intelligence.</span>
              </h1>
              <p className="text-base text-slate-600 leading-relaxed font-medium">
                Focus NDT Innovations (FNI) is the technology-driven subsidiary of Focus NDT Group, 
                dedicated to engineering the future of NDT 4.0 through our flagship engine, EvalNDT.
              </p>
            </div>
            {/* BRAND LOGO GROUPING */}
            <div className="flex flex-col items-center p-6 bg-white rounded-2xl border border-slate-200 shadow-sm">
              <div className="text-xl font-black italic tracking-tighter text-slate-900 border-b pb-2 mb-2 w-full text-center">
                FOCUS<span className="text-indigo-600">GROUP</span>
              </div>
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Parent Organization
              </div>
            </div>
          </div>
        </div>
      </section>

      <main className="max-w-6xl mx-auto py-12 px-6">
        
{/* --- 1. THE PRODUCT: EVALNDT (LINKED TO FNI) --- */}
<section className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-24 items-center">
  <div className="order-2 lg:order-1">
     <div className="relative group">
        <div className="absolute -inset-1 bg-gradient-to-r from-indigo-600 to-blue-500 rounded-3xl blur opacity-25 group-hover:opacity-40 transition duration-1000"></div>
        <div className="relative bg-slate-900 rounded-3xl overflow-hidden shadow-2xl border border-slate-800">
            {/* Top Bar Mockup */}
            <div className="bg-slate-800/50 px-5 py-3 flex items-center justify-between border-b border-slate-700/50">
                <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/40"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-500/40"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/40"></div>
                </div>
                <div className="text-[10px] font-mono text-slate-500 tracking-widest uppercase">System Active // PAUT Engine</div>
            </div>
            
            {/* Branding Core */}
            <div className="p-12 flex flex-col items-center justify-center min-h-[320px]">
                <div className="text-5xl font-black tracking-tighter text-white mb-3">
                    EVAL<span className="text-indigo-600">NDT</span>
                </div>
                <div className="h-1 w-16 bg-gradient-to-r from-indigo-500 to-blue-400 rounded-full mb-4"></div>
                <p className="text-[10px] font-black text-indigo-300/80 uppercase tracking-[0.4em] text-center">
                    Engineering Intelligence Platform
                </p>
            </div>
            
            {/* Technical Overlay Mockup */}
            <div className="absolute bottom-4 left-6 right-6 flex justify-between opacity-20">
                <Activity size={40} className="text-indigo-400" />
                <Database size={40} className="text-indigo-400" />
            </div>
        </div>
     </div>
  </div>

  <div className="space-y-8 order-1 lg:order-2">
    <div>
        <h4 className="text-indigo-600 text-xs font-black uppercase tracking-widest mb-2">The Gold Standard in NDT 4.0</h4>
        <h2 className="text-3xl md:text-4xl font-black text-slate-900 uppercase tracking-tight leading-none">
            Automating <br/> <span className="text-indigo-600">Signal Analytics.</span>
        </h2>
    </div>

    <div className="space-y-4">
        <p className="text-slate-600 text-base leading-relaxed">
            <strong>EvalNDT</strong> is a sophisticated analytical engine designed to bridge the gap between raw field acquisition and finalized engineering reports. Developed by <strong>Focus NDT Innovations</strong>, it eliminates the manual bottleneck of traditional ultrasonic data interpretation.
        </p>
        
        <p className="text-slate-600 text-sm leading-relaxed">
            By directly parsing proprietary acquisition files from leading <strong>Phased Array (PAUT)</strong> hardware, EvalNDT reconstructs high-fidelity <strong>A-Scan, B-Scan, and C-Scan</strong> visuals. This ensures a "Single Source of Truth," providing deterministic defect sizing and characterization without human subjectivity.
        </p>
    </div>

    {/* SEO-Rich Feature Grid */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-8 border-t border-slate-100 pt-8">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 text-slate-900 font-black text-xs uppercase tracking-tighter">
            <Cpu size={16} className="text-indigo-600" /> Automated Sizing
        </div>
        <p className="text-[11px] text-slate-500 leading-normal">Deterministic -6dB drop and peak amplitude detection for ISO-compliant defect measurement.</p>
      </div>
      
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 text-slate-900 font-black text-xs uppercase tracking-tighter">
            <FileCheck size={16} className="text-indigo-600" /> Code Compliance
        </div>
        <p className="text-[11px] text-slate-500 leading-normal">Instant validation against <strong>ASME Section V</strong>, API 1104, and AWS D1.1 industrial standards.</p>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 text-slate-900 font-black text-xs uppercase tracking-tighter">
            <Database size={16} className="text-indigo-600" /> Cloud Digitization
        </div>
        <p className="text-[11px] text-slate-500 leading-normal">Transforming analog site history into searchable digital assets for predictive maintenance.</p>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 text-slate-900 font-black text-xs uppercase tracking-tighter">
            <BarChart3 size={16} className="text-indigo-600" /> Performance Gains
        </div>
        <p className="text-[11px] text-slate-500 leading-normal">Reduction in technical reporting time by up to <strong>70%</strong>, accelerating project delivery cycles.</p>
      </div>
    </div>
  </div>
</section>

        {/* --- 2. THE INNOVATOR: FOCUS NDT INNOVATIONS (FNI) --- */}
{/* --- Updated FOCUS INNOVATIONS (FNI) --- */}
<section className="mb-20 pt-16 border-t border-slate-100 bg-white">
  <div className="flex flex-col md:flex-row justify-between items-end gap-6 mb-12">
    <div className="max-w-2xl border-l-4 border-[#F97316] pl-6"> {/* Brand Orange Accent */}
        <h2 className="text-3xl font-black text-[#1E3A8A] uppercase tracking-tighter mb-4"> {/* Brand Blue Header */}
           Focus NDT Innovations (FNI)
        </h2>
        <p className="text-slate-600 text-sm md:text-base leading-relaxed font-medium">
           As the specialized R&D arm, FNI develops the software architecture used across the 
           <strong>Focus NDT Group</strong>'s global operations. Our focus is on the "NDT 4.0" ecosystem—unifying site testing, data analysis, and client delivery into a single digital thread.
        </p>
    </div>
    
    {/* LOGO PLACEHOLDER: FNI (Orange/Blue synergy) */}
 <div 
  id="fni-logo-stack" 
  className="px-6 py-4 border border-slate-200 rounded-2xl bg-white/50 backdrop-blur-sm flex items-center gap-5 hover:border-[#F97316] transition-all duration-300 shadow-sm group"
>
    {/* ENHANCED LOGO CONTAINER */}
   <img 
        src={FNI} 
        alt="Focus NDT Innovations Logo" 
        title="Focus NDT Innovations"
        className="w-24 md:w-32 h-auto object-contain filter drop-shadow-sm group-hover:drop-shadow-md group-hover:scale-105 transition-all duration-500"
    />

    {/* REFINED BRAND TEXT SYNERGY */}
    <div className="flex flex-col">
        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-1 leading-none">
            Subsidiary of Focus Group
        </span>
        <div className="text-sm font-black uppercase tracking-tighter leading-[1.1] text-[#1E3A8A]">
            Focus NDT <br/>
            <span className="text-[#F97316] text-lg">Innovations</span>
        </div>
    </div>
</div>
  </div>

  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
    {[
      { title: 'Focus Portal', tag: 'Operation', desc: 'Internal management for Focus Group technicians and projects.' },
      { title: 'EvalNDT', tag: 'Product', desc: 'The flagship analytical engine for PAUT/UT signal processing.' },
      { title: 'UT Logic', tag: 'R&D', desc: 'Advanced simulation tools for focal law and beam verification.' },
      { title: 'Report Directly', tag: 'Client', desc: 'The secure gateway for clients to access Focus Group results.' }
    ].map((item, i) => (
      <div key={i} className="p-6 bg-white border border-slate-200 rounded-2xl hover:border-[#F97316] transition-all group shadow-sm">
        <span className="text-[9px] font-black uppercase text-[#F97316] mb-3 block tracking-widest leading-none">
          {item.tag}
        </span>
        <h4 className="text-base font-black text-[#1E3A8A] mb-2 flex items-center justify-between">
          {item.title} 
          <ChevronRight size={14} className="text-slate-300 group-hover:text-[#F97316] group-hover:translate-x-1 transition-all" />
        </h4>
        <p className="text-xs text-slate-500 leading-relaxed font-medium">
          {item.desc}
        </p>
      </div>
    ))}
  </div>
</section>

{/* --- 3. THE PARENT: FOCUS NDT GROUP --- */}
{/* --- 3. THE PARENT: FOCUS NDT GROUP --- */}
<section className="py-16 bg-[#111827] rounded-[2.5rem] px-8 md:px-16 text-white relative overflow-hidden mb-20 shadow-2xl border border-slate-800/50">
  <div className="relative z-10">
    <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-16">
        <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
               <LayoutGrid size={16} className="text-[#F97316]" />
               <h2 className="text-[#60A5FA] text-xs font-black uppercase tracking-[0.4em]">
                  Focus Group Ecosystem
               </h2>
            </div>
            
            <h3 className="text-3xl md:text-5xl font-black mb-6 leading-[1.1] tracking-tighter text-white">
                Bridging Field Services <br />
                with <span className="text-[#F97316] italic">Digital Intelligence.</span>
            </h3>
            
            <p className="text-slate-400 text-base leading-relaxed font-medium">
                EvalNDT is the technological heart of the **Focus NDT Group**. We operate across three primary domains to ensure industrial integrity from the field to the cloud.
            </p>
        </div>

        {/* Corporate Link to main domain */}
        <a 
          href="https://focusndtgroup.com" 
          target="_blank" 
          rel="noopener noreferrer"
          className="group flex items-center gap-2 px-5 py-2.5 bg-white/5 border border-white/10 rounded-full hover:bg-white/10 hover:border-[#F97316]/30 transition-all"
        >
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-300">Group Portal</span>
            <ExternalLink size={12} className="text-[#F97316]" />
        </a>
    </div>

    {/* TWO PILLAR STRUCTURE */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
        
        {/* VERTICAL 1: INSPECTION TECHNOLOGY (Field Services) */}
        <a 
          href="https://focusinspectiontechnology.com" 
          target="_blank"
          rel="noopener noreferrer"
          className="p-8 rounded-3xl bg-white/5 border border-white/10 hover:border-[#F97316]/50 hover:bg-[#1E3A8A]/10 transition-all duration-500 group relative overflow-hidden block"
        >
            <div className="flex justify-between items-start mb-10 relative z-10">
                <Globe className="text-[#F97316]" size={32} />
                <span className="text-[10px] font-black py-1.5 px-3.5 bg-[#F97316]/10 text-[#F97316] rounded-full uppercase tracking-widest border border-[#F97316]/20">
                    Services Division
                </span>
            </div>
            
            <div className="flex items-center gap-4 mb-6 relative z-10">
               {/* Logo Container */}
            <div className="group relative inline-block">
    {/* Standalone Logo Asset */}
    <img 
        src={FIT} 
        alt="Focus Inspection Technology" 
        className="w-auto h-16 md:h-24 object-contain filter drop-shadow-md group-hover:drop-shadow-xl group-hover:scale-105 transition-all duration-500 ease-in-out" 
    />
    
    {/* Optional: Subtle bottom glow to give it "weight" */}
    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-1/2 h-4 bg-[#F97316]/10 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
</div>
               <h4 className="text-xl font-black uppercase tracking-tight text-white group-hover:text-[#F97316] transition-colors leading-none">
                  Focus Inspection <br/><span className="text-slate-400 group-hover:text-white text-sm">Technology</span>
               </h4>
            </div>

            <p className="text-sm text-slate-400 leading-relaxed mb-8 font-medium relative z-10">
                Tier-1 NDT inspections and advanced UT/PAUT services. This division provides the real-world industrial data that powers EvalNDT.
            </p>
            
            <div className="flex items-center gap-2 text-[10px] font-black text-slate-500 uppercase tracking-widest group-hover:text-white transition-colors relative z-10">
                <span>View Services</span>
                <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </div>

            <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-[#1E3A8A]/30 blur-[60px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
        </a>

        {/* VERTICAL 2: NDT INNOVATIONS (Technology) */}
        <a 
          href="https://focusin360.com" 
          target="_blank"
          rel="noopener noreferrer"
          className="p-8 rounded-3xl bg-[#1E3A8A]/10 border border-[#1E3A8A]/30 hover:border-[#F97316]/50 hover:bg-[#1E3A8A]/20 transition-all duration-500 group relative overflow-hidden block"
        >
            <div className="flex justify-between items-start mb-10 relative z-10">
                <Cpu className="text-[#60A5FA]" size={32} />
                <span className="text-[10px] font-black py-1.5 px-3.5 bg-[#1E3A8A] text-white rounded-full uppercase tracking-widest border border-[#1E3A8A]/50">
                    Tech Division
                </span>
            </div>

            <div className="flex items-center gap-4 mb-6 relative z-10">
               {/* Logo Container */}
                  {/* Logo Container */}
            <div className="group relative inline-block">
    {/* Standalone Logo Asset */}
    <img 
        src={FNI} 
        alt="Focus Inspection Technology" 
        className="w-auto h-16 md:h-24 object-contain filter drop-shadow-md group-hover:drop-shadow-xl group-hover:scale-105 transition-all duration-500 ease-in-out" 
    />
    
    {/* Optional: Subtle bottom glow to give it "weight" */}
    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-1/2 h-4 bg-[#F97316]/10 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
</div>
               <h4 className="text-xl font-black uppercase tracking-tight text-white group-hover:text-[#F97316] transition-colors leading-none">
                  Focus NDT <br/><span className="text-slate-400 group-hover:text-white text-sm">Innovations (FNI)</span>
               </h4>
            </div>

            <p className="text-sm text-slate-300 leading-relaxed mb-8 font-medium relative z-10">
                The R&D arm and developer of the <strong>EvalNDT</strong> engine. FNI specializes in industrial automation and predictive monitoring systems.
            </p>
            
            <div className="flex items-center gap-2.5 text-[#60A5FA] group-hover:text-[#F97316] font-black text-[10px] uppercase tracking-widest transition-colors relative z-10">
                <span>Explore Tech</span>
                <ChevronRight size={14} className="group-hover:translate-x-1.5 transition-transform" />
            </div>
            
            <div className="absolute -right-8 -bottom-8 w-32 h-32 bg-[#F97316]/30 blur-[50px] rounded-full"></div>
        </a>

    </div>
  </div>

  {/* Background Branding */}
  <div className="absolute -left-10 -bottom-10 text-[15rem] font-black italic text-slate-800/10 pointer-events-none select-none">
    FOCUS
  </div>
</section>

        {/* --- 4. SEO STANDARDS & COMPLIANCE --- */}
      {/* --- 4. SEO STANDARDS & COMPLIANCE --- */}
{/* --- TRUST, STANDARDS & COMPLIANCE SECTION --- */}
<section className="py-24 bg-slate-50/50 border-t border-slate-100">
    <div className="max-w-7xl mx-auto px-8">
        
        {/* TOP TIER: INDUSTRIAL COMPLIANCE */}
        <div className="flex flex-col items-center mb-16 text-center">
            <div className="flex items-center gap-2 mb-6">
                <ShieldCheck size={18} className="text-[#1E3A8A]" />
                <h2 className="text-[11px] font-black uppercase tracking-[0.4em] text-slate-500">
                    Industrial Standards & Global Compliance
                </h2>
            </div>
            <div className="flex flex-wrap justify-center gap-3 max-w-4xl">
                {[
                    'ASME Section V', 
                    'ISO 9712 / ISO 17640', 
                    'NDT 4.0 Digital Integration', 
                    'Certified PAUT/TOFD Analysis',
                    'API 1104 Compliant'
                ].map((tag) => (
                    <span key={tag} className="px-6 py-2.5 rounded-xl border border-slate-200 text-[10px] font-black text-slate-600 uppercase tracking-tighter bg-white shadow-sm hover:border-[#F97316] hover:text-[#F97316] transition-all duration-300 cursor-default">
                        {tag}
                    </span>
                ))}
            </div>
        </div>

        {/* MIDDLE TIER: ECOSYSTEM CONNECTIVITY */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 py-12 border-y border-slate-200/60 mb-12">
            {/* The Engine Logic */}
            <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2 text-[#1E3A8A]">
                    <Cpu size={16} />
                    <h5 className="text-[10px] font-black uppercase tracking-widest">The Core Engine</h5>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed font-medium">
                    EvalNDT operates as a deterministic processing layer, ensuring high-fidelity data reconstruction for safety-critical inspections.
                </p>
            </div>

            {/* Corporate Location */}
            <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2 text-[#1E3A8A]">
                    <MapPin size={16} />
                    <h5 className="text-[10px] font-black uppercase tracking-widest">Global Operations</h5>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed font-medium">
                    Focus NDT Innovations operates from Maharashtra, India, serving the global Oil & Gas and Infrastructure sectors.
                </p>
            </div>

            {/* Group Navigation */}
            <div className="flex flex-col gap-4 md:items-end">
                <h5 className="text-[10px] font-black uppercase tracking-widest text-[#1E3A8A]">Ecosystem Portals</h5>
                <nav className="flex flex-col gap-3 md:items-end">
                    <a href="https://focusin360.com" target="_blank" className="text-[10px] font-bold text-slate-400 hover:text-[#F97316] transition-colors flex items-center gap-2 group">
                        FOCUSIN360 <ArrowUpRight size={12} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform"/>
                    </a>
                    <a href="https://focusinspectiontechnology.com" target="_blank" className="text-[10px] font-bold text-slate-400 hover:text-[#F97316] transition-colors flex items-center gap-2 group">
                        FOCUS INSPECTION <ArrowUpRight size={12} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform"/>
                    </a>
                    <a href="https://focusndtgroup.com" target="_blank" className="text-[10px] font-bold text-slate-400 hover:text-[#F97316] transition-colors flex items-center gap-2 group">
                        GROUP PORTAL <ArrowUpRight size={12} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform"/>
                    </a>
                </nav>
            </div>
        </div>

        {/* BOTTOM TIER: CORPORATE AFFILIATION */}
     
    </div>
</section>

      </main>
      <Footer />

    </div>
  );
};

export default About;